import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService"; // Using the shared Axios instance


export const countryApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/countries', { params });
    return response.data;
  },
  getByCode: async (code) => {
    const response = await api.get(`/countries/${code}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/admin/countries', data);
    return response.data;
  },
  update: async ({ id, data }) => {
    const response = await api.put(`/admin/countries/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/admin/countries/${id}`);
    return response.data;
  },
  toggleStatus: async (id) => {
    const response = await api.patch(`/admin/countries/${id}/toggle-status`);
    return response.data;
  }
};

// Query hooks
export const useCountries = (params = {}) => {
  return useQuery({
    queryKey: ['countries', params],
    queryFn: () => countryApi.getAll(params),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

export const useCountry = (code) => {
  return useQuery({
    queryKey: ['country', code],
    queryFn: () => countryApi.getByCode(code),
    enabled: !!code,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

// Admin query hooks
export const useAdminCountries = (params = {}) => {
  return useQuery({
    queryKey: ['admin-countries', params],
    queryFn: () => countryApi.getAll(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
};

// Mutation hooks
export const useCreateCountry = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: countryApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['admin-countries']);
      queryClient.invalidateQueries(['countries']);
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
};

export const useUpdateCountry = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: countryApi.update,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['admin-countries']);
      queryClient.invalidateQueries(['countries']);
      queryClient.invalidateQueries(['country', data.data?.code]);
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
};

export const useDeleteCountry = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: countryApi.delete,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['admin-countries']);
      queryClient.invalidateQueries(['countries']);
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
};

export const useToggleCountryStatus = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: countryApi.toggleStatus,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['admin-countries']);
      queryClient.invalidateQueries(['countries']);
      queryClient.invalidateQueries(['country', data.data?.code]);
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
};
